import { motion } from 'framer-motion';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { smoothScroll } from '../utils/scrolling';

export default function NavigationBar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleMobileNavClick = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  const handleConsultationClick = () => {
    setIsMobileMenuOpen(false);
    setTimeout(() => {
      smoothScroll('consultation-form');
    }, 100);
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-sm border-b ${
        theme === 'dark' 
          ? 'bg-black/80 border-blue-900/20' 
          : 'bg-white/80 border-gray-200'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link
            to="/"
            className="flex items-center gap-2"
          >
            <img 
              src="https://i.ibb.co/wxRhVj6/baslogo2.png" 
              alt="BAS Logo" 
              className="h-8 w-auto"
            />
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
              Business Automation Solutions
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-md transition-colors ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-md transition-colors ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            <div className="hidden md:flex items-center space-x-8">
              <NavLink to="/web-design" active={location.pathname === '/web-design'}>Web Design</NavLink>
              <NavLink to="/mobile-apps" active={location.pathname === '/mobile-apps'}>Mobile Apps</NavLink>
              <NavLink to="/ai-agents" active={location.pathname === '/ai-agents'}>AI Agents</NavLink>
              <NavLink to="/automation" active={location.pathname === '/automation'}>Automation</NavLink>
              <NavLink to="/lead-generation" active={location.pathname === '/lead-generation'}>Lead Gen</NavLink>
              <NavLink to="/testimonials" active={location.pathname === '/testimonials'}>Testimonials</NavLink>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => smoothScroll('consultation-form')}
                className="bg-gradient-to-r from-blue-400 to-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                Consultation
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{
          opacity: isMobileMenuOpen ? 1 : 0,
          height: isMobileMenuOpen ? 'auto' : 0
        }}
        transition={{ duration: 0.2 }}
        className={`md:hidden backdrop-blur-sm ${
          theme === 'dark'
            ? 'bg-black/95 border-b border-blue-900/20'
            : 'bg-white/95 border-b border-gray-200'
        }`}
      >
        <div className="px-4 pt-2 pb-4 space-y-2">
          <MobileNavLink onClick={() => handleMobileNavClick('/web-design')}>Web Design</MobileNavLink>
          <MobileNavLink onClick={() => handleMobileNavClick('/mobile-apps')}>Mobile Apps</MobileNavLink>
          <MobileNavLink onClick={() => handleMobileNavClick('/ai-agents')}>AI Agents</MobileNavLink>
          <MobileNavLink onClick={() => handleMobileNavClick('/automation')}>Automation</MobileNavLink>
          <MobileNavLink onClick={() => handleMobileNavClick('/lead-generation')}>Lead Generation</MobileNavLink>
          <MobileNavLink onClick={() => handleMobileNavClick('/testimonials')}>Testimonials</MobileNavLink>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleConsultationClick}
            className={`w-full text-left px-4 py-2 font-semibold rounded-lg transition-colors ${
              theme === 'dark'
                ? 'text-blue-400 hover:bg-blue-500/10'
                : 'text-blue-600 hover:bg-blue-50'
            }`}
          >
            Schedule Consultation
          </motion.button>
        </div>
      </motion.div>
    </motion.nav>
  );
}

function NavLink({ to, active, children }: { to: string; active: boolean; children: React.ReactNode }) {
  const { theme } = useTheme();
  
  return (
    <Link
      to={to}
      className={`transition-colors ${
        active
          ? theme === 'dark'
            ? 'text-blue-400'
            : 'text-blue-600'
          : theme === 'dark'
          ? 'text-gray-300 hover:text-white'
          : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      {children}
    </Link>
  );
}

function MobileNavLink({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  const { theme } = useTheme();
  
  return (
    <button
      onClick={onClick}
      className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${
        theme === 'dark'
          ? 'text-gray-300 hover:text-white hover:bg-gray-800'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}